/* eslint-disable immutable/no-mutation */
import { useCallback, useEffect, useMemo } from 'react';
import useOnClickOutside from '~/client/hooks/events/useOnClickOutside';
import useFeature from '~/client/hooks/feature/useFeature';
import useDidMount from '~/client/hooks/lifecycle/useDidMount';
import useRouter from '~/client/hooks/router/useRouter';
import useAgentData from '~/client/hooks/useAgentData';
import useSite from '~/client/hooks/useSite';
import useThemeSwitcher from '~/client/shared/ThemeSwitcher/useThemeSwitcher';
import useLoyaltyStore from '~/client/store/useLoyaltyStore';
import LANGUAGE_COOKIE from '~/shared/cookies/LANGUAGE_COOKIE';
import Cookie from '~/shared/utils/Cookie';
import getEnv from '~/shared/utils/getEnv';
import isSsr from '~/shared/utils/isSsr';
import useLoyaltyInAppLinkClicked from './useLoyaltyInAppLinkClicked';
import containerId from '../constant/containerId';

const useLoyalty = () => {
  const { isMobile } = useAgentData();
  const { isLoyaltyOpen, setLoyaltyOpen, setLoyaltyWidget, isWidgetOpen } =
    useLoyaltyStore();
  const isLoyaltyMicroFrontendEnabled = useFeature(
    'isLoyaltyMicroFrontendEnabled',
  );
  const { handleLoyaltyInAppLinkClicked } = useLoyaltyInAppLinkClicked();

  const { site, isBitcasino } = useSite();
  const { query } = useRouter();
  const loyaltyParam = !!query?.['l'];
  const { colorScheme } = useThemeSwitcher();
  const locale = Cookie.get(LANGUAGE_COOKIE);

  const config = useMemo(
    () => ({
      productName: site,
      language: locale,
      theme: colorScheme,
      graphqlUrl: getEnv(
        'GRAPHQL_SERVER_URL',
        'https://bitcasino.t2.testenv.io/graphql',
      ),
    }),
    [site, locale, colorScheme],
  );

  const mountLoyaltyWidget = useCallback(() => {
    if (!isLoyaltyMicroFrontendEnabled) {
      return;
    }
    if (!isSsr() && window.Loyalty && !isWidgetOpen) {
      window.Loyalty.mount?.({
        containerId: containerId.widget,
        componentName: 'widget',
        config,
      });
      setLoyaltyWidget({ isWidgetOpen: true });
    }
  }, [setLoyaltyWidget, config, isWidgetOpen, isLoyaltyMicroFrontendEnabled]);

  const unMountLoyaltyWidget = useCallback(() => {
    if (!isSsr() && window.Loyalty && isWidgetOpen) {
      window.Loyalty.unmount?.({
        containerId: containerId.widget,
        componentName: 'widget',
      });
      setLoyaltyWidget({ isWidgetOpen: false });
    }
  }, [setLoyaltyWidget, isWidgetOpen]);

  const mountLoyaltyApp = useCallback(() => {
    if (isSsr() || !window.Loyalty || isLoyaltyOpen) {
      return;
    }
    window.Loyalty?.mount?.({
      containerId: containerId.app,
      componentName: 'app',
      config,
    });
    setLoyaltyOpen({ isLoyaltyOpen: true });
  }, [setLoyaltyOpen, config, isLoyaltyOpen]);

  useDidMount(() => {
    if (loyaltyParam && !isLoyaltyOpen) {
      setLoyaltyOpen({ isLoyaltyOpen: true });
    }
  });
  const unMountLoyaltyApp = useCallback(() => {
    window.Loyalty.unmount({
      containerId: containerId.app,
      componentName: 'app',
    });
    setLoyaltyOpen({ isLoyaltyOpen: false });
  }, [setLoyaltyOpen]);

  const { elementRef } = useOnClickOutside({
    handler: () => {
      if (isMobile) {
        return;
      }
      if (!isSsr() && window.Loyalty) {
        unMountLoyaltyApp();
      }
    },
  });

  useEffect(() => {
    if (isLoyaltyOpen) {
      mountLoyaltyApp();
    }
    if (!isSsr() && window.Loyalty) {
      window.Loyalty.onClosed = () => {
        unMountLoyaltyApp();
      };

      window.Loyalty.onLinkClicked = (link: LinkType) => {
        handleLoyaltyInAppLinkClicked(link);
      };
    }
  }, [
    isLoyaltyOpen,
    config,
    setLoyaltyOpen,
    unMountLoyaltyApp,
    mountLoyaltyApp,
    handleLoyaltyInAppLinkClicked,
  ]);

  const loyaltyUrl = useMemo(
    () => ({
      url: getEnv(
        'LOYALTY_URL',
        'https://d1bgsfv6q3t82k.cloudfront.net/loyalty.js',
      ),
    }),
    [],
  );
  return {
    site,
    isLoyaltyOpen,
    setLoyaltyOpen,
    elementRef,
    loyaltyUrl,
    mountLoyaltyApp,
    mountLoyaltyWidget,
    unMountLoyaltyWidget,
    isBitcasino,
    config,
    isLoyaltyMicroFrontendEnabled,
  };
};

export default useLoyalty;
